/**
 * @fileoverview User schemas for user validation and type definitions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

// Base User schema without defaults (for partial updates)
const BaseUserSchema = z.object({
  id: z.string().uuid().openapi({
    description: 'Unique identifier for the user',
    example: '550e8400-e29b-41d4-a716-446655440000',
  }),
  email: z.string().email().openapi({
    description: 'Email address of the user',
    example: 'user@example.com',
  }),
  name: z.string().openapi({
    description: 'Display name of the user',
    example: 'John Doe',
  }),
  passwordHash: z.string().openapi({
    description: 'Hashed password (never exposed in API responses)',
  }),
  createdAt: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the user was created',
    example: '2024-01-15T10:30:00Z',
  }),
  updatedAt: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the user was last updated',
    example: '2024-01-15T14:45:00Z',
  }),
});

// Full User schema with defaults (for creation and storage)
export const UserSchema = BaseUserSchema.openapi('User');

// Create schema - omit auto-generated fields
export const CreateUserSchema = UserSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).openapi('CreateUser');

// Update schema - partial from BASE to avoid applying defaults
// Uses explicit field definitions to ensure proper validation (e.g., min length on name)
export const UpdateUserSchema = z
  .object({
    name: z.string().min(1).optional().openapi({
      description: 'Updated display name',
      example: 'Jane Doe',
    }),
    email: z.string().email().optional().openapi({
      description: 'Updated email address',
      example: 'newemail@example.com',
    }),
  })
  .openapi('UpdateUser');

export const UserResponseSchema = UserSchema.omit({ passwordHash: true }).openapi('UserResponse');

export const UserListResponseSchema = createListResponseSchema(
  UserResponseSchema,
  'users',
  'UserListResponse'
);

/**
 * JSDoc type definition for User - User account type
 * @typedef {Object} UserType
 * @property {string} id - Unique identifier for the user
 * @property {string} email - Email address of the user
 * @property {string} name - Display name of the user
 * @property {string} passwordHash - Hashed password (never exposed in API responses)
 * @property {string} createdAt - ISO 8601 timestamp when the user was created
 * @property {string} updatedAt - ISO 8601 timestamp when the user was last updated
 */

export const UserType = /** @type {UserType} */ ({});
